// FMD0000 Dispatch Management (DEV-6402) - board-wide constants.
//
// Everything here maps one-to-one onto something in the Delphi. Where it does, the source is named,
// because most of these are single characters whose meaning is not guessable from the value.

/** Top tabs. Only the three migrated in this slice; the other ten are later tickets. */
export const TOP_TABS = {
    PICK: "pick",
    DEL: "del",
    CURRENT: "current",
};

/** Bottom tabs. */
export const BOTTOM_TABS = {
    DETAILS: "details",
    EMPTY_CONTAINER: "emptyContainer",
    CONTENTS: "contents",
    PICK: "bottomPick",
    DEL: "bottomDel",
    TRIPS: "bottomTrips",
    TRUCKS: "bottomTrucks",
    TRAILERS: "bottomTrailers",
    DRIVERS: "bottomDrivers",
    SHIPPERS: "bottomShippers",
    MESSAGES: "bottomMessages",
    HIST_SHIP: "histShip",
    HIST_TRIP: "histTrip",
    TOTALS: "totals",
};

/**
 * The bottom tabs Pick, Del and Current reveal, in the Delphi's own strip order, landing on Details.
 *
 * From uFMD0000.pas SetBottomTabVisible (line 6413). That routine explicitly enables thirteen tabs for
 * these three top tabs - and the running screen shows fourteen. The fourteenth is Del:
 * `tabBottomDelDispatch` is the one bottom tab the routine never touches, so it keeps its DFM default
 * of TabVisible = True and is permanently visible. That looks like an oversight in the Delphi rather
 * than intent, but it is what the desktop shows, so it is what the web shows.
 *
 * Kept as a table rather than a conditional so the remaining top tabs are added by extending this
 * object, not by editing control flow.
 */
export const BOTTOM_TAB_VISIBILITY = (() => {
    const dispatchObjectTabs = {
        visible: [
            BOTTOM_TABS.DETAILS,
            BOTTOM_TABS.EMPTY_CONTAINER,
            BOTTOM_TABS.CONTENTS,
            BOTTOM_TABS.PICK,
            BOTTOM_TABS.DEL,
            BOTTOM_TABS.TRIPS,
            BOTTOM_TABS.TRUCKS,
            BOTTOM_TABS.TRAILERS,
            BOTTOM_TABS.DRIVERS,
            BOTTOM_TABS.SHIPPERS,
            BOTTOM_TABS.MESSAGES,
            BOTTOM_TABS.HIST_SHIP,
            BOTTOM_TABS.HIST_TRIP,
            BOTTOM_TABS.TOTALS,
        ],
        landing: BOTTOM_TABS.DETAILS,
    };

    return {
        [TOP_TABS.PICK]: dispatchObjectTabs,
        [TOP_TABS.DEL]: dispatchObjectTabs,
        [TOP_TABS.CURRENT]: dispatchObjectTabs,
    };
})();

/**
 * Which bottom tabs are scoped to the selected top row.
 *
 * These three. The rest are independent reference lists - in the Delphi they are what a dispatcher
 * drags between the panes, and pagBottomDispatchChange simply reopens them (uFMD0000.pas:8612).
 * Filtering them by the selected row would be a behaviour change, not a fix. Totals is neither: it is
 * computed from the selection client-side.
 *
 * Messages is the one tab where "independent list by design" does not hold, and it belongs here:
 * uFMD0000.pas:4840 assigns `FMDispatchObject.RowID` and the `DispatchRowID` parameter on every
 * board-row scroll, and both halves of uFMM0042's UNION ALL filter on it.
 */
export const ROW_SCOPED_BOTTOM_TABS = [
    BOTTOM_TABS.DETAILS,
    BOTTOM_TABS.CONTENTS,
    BOTTOM_TABS.MESSAGES,
];

/**
 * FMDISPATCHINGDP.DISPATCHINGTABDEFAULT to top tab.
 *
 * An unrecognised or absent value lands on Pick, matching the Delphi's ActivePageDefault of
 * tabTopDispatch. Values for the unmigrated tabs deliberately fall through to that default rather
 * than landing nowhere.
 */
export const TAB_DEFAULT_TO_TOP_TAB = {
    P: TOP_TABS.PICK,
    D: TOP_TABS.DEL,
    C: TOP_TABS.CURRENT,
};

/**
 * FMDISPATCHINGDP.TABDEFAULTS - which top tabs the profile makes available.
 *
 * A different column from DISPATCHINGTABDEFAULT above, and it does a different job: that one picks
 * the landing tab, this one hides tabs outright. From SetDispatchingTabs (uFMD0000.pas:10648), which
 * starts with every tab visible and then removes:
 *
 *   'P' -> hides top Del       (Pick and Current remain)
 *   'D' -> hides top Pick      (Del and Current remain)
 *   'C' -> hides top Pick and top Del  (Current alone)
 *   'A', absent, anything else -> all three
 *
 * Current is never hidden by this column, which is why it has no entry: the Delphi has no code path
 * that removes it. A profile is therefore never left with no top tab at all.
 */
export const TAB_DEFAULT_HIDDEN_TOP_TABS = {
    P: [TOP_TABS.DEL],
    D: [TOP_TABS.PICK],
    C: [TOP_TABS.PICK, TOP_TABS.DEL],
};

/**
 * FMDISPATCHINGDP.DRIVERTABDEFAULT - what the Drivers tab is called.
 *
 * 'C' renames it "Carrier" on both strips (uFMD0000.pas:10687 assigns tabDriversTop.Caption and
 * tabBottomDrivers.Caption together); 'D', absent or anything else leaves it "Drivers". Only the
 * bottom strip exists in this slice, so only that caption is applied - but the value is read from
 * the same place, so the top strip gets it for free when those tabs are migrated.
 */
export const DRIVER_TAB_TITLES = {
    C: "Carrier",
    D: "Drivers",
};

/** What the Drivers tab is called when the profile says nothing. */
export const DRIVER_TAB_TITLE_DEFAULT = DRIVER_TAB_TITLES.D;

/**
 * The single-character colour codes FMDISPATCHINGDP stores, as resolved by
 * TfrmFMD0000.ColorSelection (uFMD0000.pas:1713).
 *
 * The value is the CSS colour the class in dispatchManagement.css paints. 'Z' is the window default
 * and is also what the API substitutes for null, so it means "leave the row alone" - it is absent
 * from this map on purpose, and an unmapped code is treated the same way.
 */
export const DISPATCH_COLOR_CODES = {
    1: "aqua",
    2: "blue",
    3: "fuchsia",
    4: "green",
    5: "lime",
    6: "maroon",
    7: "navy",
    8: "olive",
    9: "purple",
    R: "red",
    S: "silver",
    T: "teal",
    W: "white",
    Y: "yellow",
};

/** The 'no colour' code - ISNULL(...,'Z') in the legacy profile query. */
export const DISPATCH_COLOR_DEFAULT = "Z";

/** FMDISPATCHINGDP.CURRENTTABCOLORS - the Current tab only paints colours when this is on. */
export const CURRENT_TAB_COLORS_ON = "Y";

/**
 * FMORDERS.DISPATCHINGMETHOD for a consolidated order.
 *
 * The bottom Details tab sends this as its `consolidated` flag: a consolidated order lists the whole
 * order's dispatch objects, anything else lists the selected object's own trip stops. Mirrors
 * DispatchManagementConstants.ConsolidatedDispatchingMethod on the API side. The 'S' (single) value
 * is not declared here - the client only ever asks "is it consolidated", and the server owns the
 * ISNULL(...,'S') fallback.
 */
export const CONSOLIDATED_DISPATCHING_METHOD = "C";

/**
 * Which band a row falls in. Warning wins over pending, as in the Delphi, where the pending test sits
 * in the else branch of the warning test.
 */
export const ROW_BAND = {
    WARNING: "warning",
    PENDING: "pending",
    NONE: "none",
};

/**
 * Per-tab colouring inputs: which DTO date the bands are measured against, and which pair of profile
 * levels sets them. From grdTopDispatchCustomDraw (uFMD0000.pas:7442) and its Del sibling
 * (grdTopDelDispatchCustomDraw, uFMD0000.pas:9547).
 *
 * The Current tab is deliberately absent. It does not band by date at all - see
 * CURRENT_TAB_COLOUR_RULE below - so giving it a date basis here is what made it paint the same
 * colours as Pick.
 */
export const TAB_COLOR_BASIS = {
    [TOP_TABS.PICK]: {
        dateField: "ScheduledPickupDt",
        warningLevelField: "WarningLevel",
        pendingLevelField: "PendingLevel",
    },
    [TOP_TABS.DEL]: {
        dateField: "ScheduledDeliveryDt",
        warningLevelField: "DoDelWarningLevel",
        pendingLevelField: "DoDelPendingLevel",
    },
};

/**
 * The Current tab's colouring rule, which is not the date-threshold rule the other two tabs use.
 *
 * From grdTopCurrentDispatchCustomDraw (uFMD0000.pas:9694), which colours by trip assignment and
 * trip status and never looks at a date or a PENDINGLEVEL/WARNINGLEVEL threshold:
 *
 *   CURRENTTABCOLORS <> 'Y'            -> no colouring at all
 *   R_TRIPHEADER is null               -> window default (no colour) - not yet on a trip
 *   on a trip, TRIPSTATUS  = 'P'       -> pending colour  (planned)
 *   on a trip, TRIPSTATUS <> 'P'       -> warning colour  (already moving)
 *
 * Documented as a constant rather than only in code because it is the one place the three tabs
 * genuinely diverge, and the natural assumption - that Current bands like Pick - is wrong.
 */
export const CURRENT_TAB_COLOUR_RULE = "tripAssignment";

/**
 * The key field a grid and its store agree on.
 *
 * Two spellings, and which one a grid needs is not a preference: two DTO generations feed this
 * board. Its own DTOs are PascalCase, while TripDetailDTO and TripHeaderDTO - reused rather than
 * duplicated - are the legacy all-caps shape. Naming both is what stops a grid from being pointed at
 * the spelling its store does not return, which fails as an unkeyed grid rather than as an error.
 */
export const KEY_FIELD = {
    /** The board's own DTOs: DispatchObjectBoardDTO, DispatchObjectContentsDTO, and the rest. */
    ROW_ID: "RowId",

    /** The legacy all-caps DTOs: TripDetailDTO (Details) and TripHeaderDTO (Trips, Hist Trip). */
    LEGACY_ROW_ID: "ROWID",
};

/**
 * The grid layout every grid on the board opens on, before the user picks a saved one.
 *
 * The name is what GridLayoutManager round-trips to the server, so it is the same literal the rest
 * of the app uses - see DEFAULT_LAYOUT_NAME in ar/components/collectionsCredit/constants.js.
 */
export const DEFAULT_LAYOUT_NAME = "Default";

/**
 * Grid layout keys. One fresh GUID per grid, never shared: GridLayout.sendStorageRequest keys the
 * saved column layout on these, so two grids sharing a key would overwrite each other's layout.
 */
export const GRID_KEYS = {
    // top
    PICK: "b1f4c8e2-3a67-4d19-9c05-7e2a48bd6f31",
    DEL: "c27ad590-6b41-4e83-a1f7-05d93c8e2b64",
    CURRENT: "d3e81b46-9f52-4a07-b6c8-1a47e0d95382",
    // bottom
    DETAILS: "e4c92d7a-1b83-4f56-8d20-9c61b3f7a048",
    EMPTY_CONTAINER: "f508a3c1-7d64-4b92-85ef-2c9047b1e8d3",
    CONTENTS: "a61b7e94-2c58-4d03-9f71-8b40e2c96d15",
    BOTTOM_PICK: "b72c8fa5-3d69-4e14-a082-9c51f3d07e26",
    BOTTOM_DEL: "c83d90b6-4e7a-4f25-b193-0d62a4e18f37",
    BOTTOM_TRIPS: "d94ea1c7-5f8b-4036-c2a4-1e73b5f29048",
    BOTTOM_TRUCKS: "ea05b2d8-6a9c-4147-d3b5-2f84c6031159",
    BOTTOM_TRAILERS: "fb16c3e9-7bad-4258-e4c6-3095d714226a",
    BOTTOM_DRIVERS: "0c27d4fa-8cbe-4369-f5d7-41a6e825337b",
    BOTTOM_SHIPPERS: "1d38e50b-9dcf-447a-06e8-52b7f936448c",
    BOTTOM_MESSAGES: "2e49f61c-aed0-458b-17f9-63c80a47559d",
    HIST_SHIP: "3f5a072d-bfe1-469c-820a-74d91b5866ae",
    HIST_TRIP: "406b183e-c0f2-47ad-391b-85ea2c6977bf",
};

/** Dynamic-form identity for the Totals panel, so its layout can be customised like any other form. */
export const TOTALS_LAYOUT_ID = "B745845A-36C1-40A5-9D05-161FEE1CD98B";
export const TOTALS_LAYOUT_NAME = "FM Dispatch Management - Totals";

/** Field names for the Totals form. Local to the panel - these are computed, not stored anywhere. */
export const TOTALS_FIELD = {
    Items: "TotalItems",
    Weight: "TotalWeight",
    Cubes: "TotalCubes",
    Pallets: "TotalPallets",
    FreightRevenue: "TotalFreightRevenue",
    MerchandiseRevenue: "TotalMerchandiseRevenue",
};

/**
 * Page sizes offered by every grid on the board.
 *
 * No "all" entry, which most grids in the app do offer. Every grid here pages on the server, and "all"
 * is the one selection that opts out of it: DevExtreme drops `take` from the request, so the API
 * returns the whole result set in one response. On this board that set is FMDISPATCHOBJECT behind a
 * fifteen-table join - the largest interactive query in the product - and the cost lands on the
 * database, the wire and the browser at once. 500 is already past what anyone reads in a page.
 */
export const ALLOWED_PAGE_SIZES = [10, 20, 100, 500];

/**
 * The pattern every date and datetime column on the board renders with, bar one.
 *
 * Unpadded month, day and hour - `M/d/yyyy h:mm:ss a`, not `MM/dd/yyyy hh:mm:ss a`. This is the
 * Delphi's, not a preference: a TAbDateTimeField with no DisplayFormat is written by
 * DateTimeToString with a blank format, which uses the 'c' specifier - ShortDateFormat followed by
 * LongTimeFormat - and under the US locale those are `m/d/yyyy` and `h:mm:ss AMPM`. The desktop
 * renders `4/24/2017 8:21:00 AM`; the padded pattern rendered `04/24/2017 08:21:00 AM`.
 *
 * The exception is FMMESSAGES.MESSAGEDATE, the one column on the board whose data module states a
 * DisplayFormat - see MESSAGE_DATE_PATTERN.
 */
const DATE_TIME_PATTERN = "M/d/yyyy h:mm:ss a";

/**
 * FMMESSAGES.MESSAGEDATE on the bottom Messages tab, and nothing else.
 *
 * uFMM0042 declares `DisplayFormat = 'mm/dd/yyyy hh:mm'` on it: zero-padded date, 24-hour clock, no
 * seconds. It is the only datetime field on any of the board's modules with an explicit
 * DisplayFormat, so it is the only one that departs from DATE_TIME_PATTERN above.
 */
const MESSAGE_DATE_PATTERN = "MM/dd/yyyy HH:mm";

/**
 * Drops the time from a date-time that falls exactly on midnight.
 *
 * The Delphi's blank-format path resolves to the 'c' specifier, and 'c' is documented to omit the
 * time when the value indicates midnight precisely. So a dispatch object whose SCHEDULEDPICKUPDT is
 * stored to the day reads `4/24/2017` on the desktop, where the web was printing
 * `4/24/2017 12:00:00 AM` - a time the record does not carry, on a board where many rows are dated
 * to the day.
 *
 * Applied through `customizeText` rather than by splitting the columns into date and datetime sets:
 * which rows carry a time is a property of the data, not of the column. SCHEDULEDPICKUPDT holds
 * 08:21:00 on one row and midnight on the next.
 */
const dropMidnightTime = (cell) => {
    const value = cell?.value;

    // Only the rendered cell and what is exported from it. The filter row and header filter get the
    // full text: their editors parse what they display, and handing them a date where the column is
    // typed datetime makes the filter they build disagree with the column.
    if (cell?.target && cell.target !== "row" && cell.target !== "group") {
        return cell.valueText;
    }

    if (!(value instanceof Date)) {
        // Already formatted, or null. `cell.valueText` is what DevExtreme would have shown.
        return cell?.valueText ?? "";
    }

    const isMidnight =
        value.getHours() === 0 &&
        value.getMinutes() === 0 &&
        value.getSeconds() === 0 &&
        value.getMilliseconds() === 0;

    if (!isMidnight) {
        return cell.valueText;
    }

    // Same field order as DATE_TIME_PATTERN's date half, unpadded.
    return `${value.getMonth() + 1}/${value.getDate()}/${value.getFullYear()}`;
};

/**
 * Column display types, shared by every grid on the board so a field reads the same on a main tab
 * and on a sub tab.
 *
 * Each one is a legacy display setting, not a preference. In the Delphi every grid column is a
 * persistent field on its data module, and two of that field's properties decide how it renders:
 *
 *   DisplayFormat  - ",0.00" on a TAbFloatField, ",0" on the few that show whole units. Absent on
 *                    TAbCurrencyField, which the framework renders as money. On a TAbDateTimeField
 *                    it is almost always absent too, and an absent DisplayFormat is what sends the
 *                    field through DateTimeToString's blank-format path - see DATE_TIME_PATTERN.
 *                    FMMESSAGES.MESSAGEDATE is the single exception on this screen.
 *
 * There is no per-field "shows a time" flag to copy. `DateFloat` looks like one and is not: it
 * defaults to dfDate, and the only code that reads it is the filter panel's where-builder
 * (uAbWhereBuilder.pas:236, uAbWhereForm.pas:1680), which uses it to decide whether a filter editor
 * offers a time. No grid reads it. So the split below cannot be assigned from the legacy field
 * definitions at all - one pattern serves every moment on the board, and whether a given row shows a
 * time is decided per value by dropMidnightTime, which is how the desktop decides it too.
 *
 * DATE and DATETIME therefore render identically. They stay separate because dataType still differs,
 * and that is what the filter row reads: a calendar date filters by day, a moment filters to the
 * second. DATE goes on the columns that hold calendar dates (Call in Date, the user-defined and
 * audit dates, the vessel and notification dates) and DATETIME on the moment-in-time stamps
 * (scheduled and actual pickup, delivery, arrival, departure, appointment, trip start and finish).
 */
export const COLUMN_FORMAT = {
    /**
     * A calendar date. Same rendered pattern as DATETIME - but dataType stays "date" so the filter
     * row offers a date picker and a filter on one day matches the whole day rather than one instant.
     */
    DATE: { dataType: "date", format: DATE_TIME_PATTERN, customizeText: dropMidnightTime },

    /** A moment in time: filterable and sortable to the second. */
    DATETIME: { dataType: "datetime", format: DATE_TIME_PATTERN, customizeText: dropMidnightTime },

    /**
     * FMMESSAGES.MESSAGEDATE, and nothing else on the board - the one field whose data module states
     * a DisplayFormat. No customizeText: the Delphi's midnight rule belongs to the blank-format path,
     * and this field does not take it.
     */
    MESSAGE_DATE: { dataType: "datetime", format: MESSAGE_DATE_PATTERN },

    /** TAbIntegerField, and the TAbFloatFields whose DisplayFormat is ",0". */
    INT: { dataType: "number", format: { type: "fixedPoint", precision: 0, useGrouping: true } },

    /** TAbFloatField - DisplayFormat ",0.00". */
    DEC: { dataType: "number", format: { type: "fixedPoint", precision: 2, useGrouping: true } },

    /** TAbCurrencyField, and the totals the board form shows as "$,0.00;$,0.00-". */
    MONEY: {
        dataType: "number",
        format: { type: "currency", precision: 2, currency: "USD", useGrouping: true },
    },
};

// Coded fields render as descriptive lookups, not raw codes (Architecture.md rule 3).
//
// Every pair below is the Codes.Strings / Items.Strings of the matching persistent field in the
// module named on it. They are grouped by the TABLE the code belongs to, because the same column name
// means different things on different tables and mixing them up renders wrong text rather than no
// text: FMDISPATCHOBJECT.TRIPTYPE is D/T/S while FMTRIPHEADER.TRIPTYPE is P/D/L/B/M/H, and code 'D'
// is a valid member of both lists with unrelated meanings.
//
// Where the board's modules disagree about one code's label, the note on that list says which module
// was followed and why. Where no module defines a pair at all - FMDISPATCHOBJECT.OBJECTTYPE, and
// FMORDERS.DISPATCHINGMETHOD, which the API knows as C/S but the Delphi never labels - the column
// ships as text: inventing a list would be worse than showing the stored value.

// ---- FMDISPATCHOBJECT ---------------------------------------------------------------------------

/**
 * FMDISPATCHOBJECT.STATUS (uFMM0058 / uFMM0061).
 *
 * uFMM0036 (Pick) lists the same four codes with 'Z' spelled "Comitted" and C/H in the other order;
 * uFMM0058 (Current) and uFMM0061 (Del) both spell it "Committed". One list serves all three tabs
 * here, so the two-module spelling wins - and it happens to be the correct one. uFMM0046 (Hist Ship)
 * declares only O and H, a subset, so it needs no list of its own.
 */
export const DISPATCH_STATUS_OPTIONS = [
    { id: "O", text: "Open" },
    { id: "H", text: "Hold" },
    { id: "C", text: "Cancelled" },
    { id: "Z", text: "Committed" },
];

/**
 * FMDISPATCHOBJECT.PAYBY (uFMM0036 / uFMM0058 / uFMM0061 / uFMM0027).
 *
 * uFMM0046 (Hist Ship) alone abbreviates 'P' to "PrePay"; the other four modules say "Prepaid".
 */
export const PAY_BY_OPTIONS = [
    { id: "B", text: "Bill Third Party" },
    { id: "C", text: "Collect" },
    { id: "D", text: "COD" },
    { id: "P", text: "Prepaid" },
];

/**
 * FMDISPATCHOBJECT.TRIPTYPE and TRIPTYPE2 (uFMM0036 / uFMM0058 / uFMM0061 - all agree).
 *
 * Not the trip header's trip type - see TRIP_HEADER_TRIP_TYPE_OPTIONS.
 */
export const TRIP_TYPE_OPTIONS = [
    { id: "D", text: "Direct PU" },
    { id: "T", text: "Separate Carrier" },
    { id: "S", text: "Shipper Dropoff" },
];

/**
 * FMDISPATCHOBJECT.CONTAINERRETURN and CREATETRIPSEGMENTS (uFMM0061).
 *
 * uFMM0004 spells the CREATETRIPSEGMENTS pair out as "Yes, Create Trip Segments" / "No, Do not
 * Create Trip Segments". Kept short here: that module is a data-entry form where the sentence is the
 * control's whole label, whereas these are grid cells under a column already captioned
 * "Create Trip Seg", and uFMM0061 labels the identical Y/N flag next door simply Yes/No.
 */
export const YES_NO_OPTIONS = [
    { id: "Y", text: "Yes" },
    { id: "N", text: "No" },
];

/**
 * FMDISPATCHOBJECT.FBSTATUS (uFMM0001).
 *
 * Taken from uFMM0001 specifically because it is the module whose FBSTATUS is
 * <b>FMDISPATCHOBJECT</b>.FBSTATUS, which is the column this board binds. uFMM0004 and uFMM0094
 * declare an all-but-identical list - H/O/<b>L</b> - for <b>FMORDERS</b>.FBSTATUS, a different
 * column: the third code is 'R' here and 'L' there. Using the wrong one leaves every
 * ready-to-commit row blank.
 */
export const FB_STATUS_OPTIONS = [
    { id: "H", text: "Hold" },
    { id: "O", text: "Open" },
    { id: "R", text: "Ready to Commit Later" },
];

/** FMDISPATCHOBJECT.PODSTATUS (uFMM0046). */
export const POD_STATUS_OPTIONS = [
    { id: "N", text: "Not Required" },
    { id: "R", text: "Received" },
    { id: "Q", text: "Required" },
    { id: "M", text: "Missing" },
    { id: "A", text: "Not Available" },
];

// ---- FMORDERS -----------------------------------------------------------------------------------

/**
 * FMORDERS.ORDERTYPE (uFMM0061 / uFMM0027).
 *
 * 'M' is the value the Pick tab's ORDERDATETIME rule keys on - the Delphi shows an order date only
 * for 'M' orders (DispatchManagementConstants.MasterOrderType on the API side).
 */
export const ORDER_TYPE_OPTIONS = [
    { id: "S", text: "Single" },
    { id: "M", text: "Multiple" },
];

// ---- FMTRIPHEADER -------------------------------------------------------------------------------

/**
 * FMTRIPHEADER.STATUS (uFMM0058), which the Current tab surfaces as its TripStatus column and the
 * bottom Trips / Hist Trip grids as their Status column - one column, so one list.
 *
 * uFMM0038 (Trips) labels 'Z' "Completed/Closed" where uFMM0058 says "Committed". uFMM0058 is
 * followed because the migrated Current tab is its query, and because the alternative is the same
 * code reading two ways on one screen.
 *
 * 'P' also carries behaviour: it is the status the Current tab paints pending rather than warning
 * (CURRENT_TAB_COLOUR_RULE).
 */
export const TRIP_STATUS_OPTIONS = [
    { id: "P", text: "Planned" },
    { id: "R", text: "In Progress" },
    { id: "S", text: "Suspended/Hold" },
    { id: "T", text: "Completed/Open" },
    { id: "Z", text: "Committed" },
];

/** FMTRIPHEADER.STATUS for a planned trip. */
export const TRIP_STATUS_PLANNED = "P";

/**
 * FMTRIPHEADER.TRIPTYPE (uFMM0038) - six codes, and none of them means what the same letter means in
 * TRIP_TYPE_OPTIONS above. 'D' is "Delivery Only" here and "Direct PU" there.
 */
export const TRIP_HEADER_TRIP_TYPE_OPTIONS = [
    { id: "P", text: "Pickup Only" },
    { id: "D", text: "Delivery Only" },
    { id: "L", text: "Line Haul" },
    { id: "B", text: "TL Pickup & Delivery" },
    { id: "M", text: "Mixed" },
    { id: "H", text: "DeadHead" },
];

/** FMTRIPHEADER.PARTICIPANTPAYTYPE (uFMM0038). */
export const PARTICIPANT_PAY_TYPE_OPTIONS = [
    { id: "T", text: "Trip" },
    { id: "L", text: "Load" },
];

// ---- FMTRIPDETAIL -------------------------------------------------------------------------------

/** FMTRIPDETAIL.STATUS (uFMM0037) - only two codes, not the dispatch object's four. */
export const TRIP_DETAIL_STATUS_OPTIONS = [
    { id: "O", text: "Open" },
    { id: "H", text: "Hold" },
];

/** FMTRIPDETAIL.TRIPREQUIRED (uFMM0037), spelled out as the Delphi does. */
export const TRIP_REQUIRED_OPTIONS = [
    { id: "Y", text: "Yes, Create a Trip" },
    { id: "N", text: "No, Do not Create a Trip" },
    { id: "A", text: "N/A" },
];

// ---- IDEQUIPMENT --------------------------------------------------------------------------------

/**
 * IDEQUIPMENT.STATUS (uFMM0039 Trucks / uFMM0044 Trailers) - the bottom Trucks and Trailers grids.
 *
 * Five codes, and none of them overlaps the dispatch object's O/H/C/Z. The desktop grid shows these
 * words, so a raw 'A' in this column is the most visible instance of rule 3 on the board.
 * uFMM0044 capitalises 'D' as "Disposed Of"; uFMM0039 as "Disposed of", which is what is used here
 * since both grids share one list.
 */
export const EQUIPMENT_STATUS_OPTIONS = [
    { id: "A", text: "Active/Open" },
    { id: "C", text: "Active/Committed" },
    { id: "T", text: "Inactive/Temporary" },
    { id: "I", text: "Inactive/Permanent" },
    { id: "D", text: "Disposed of" },
];

/**
 * IDEQUIPMENT.EQUIPMENTTYPE (uIDM0002), including the Delphi's own spelling of "Miscellanous".
 *
 * Seven codes, of which this board's own constants name only two - the Trucks grid filters to 'P'
 * and Trailers to 'T', so the column reads as a constant on either tab. Mapped anyway: it is a coded
 * field offered in the column chooser, and the full list is what makes the two board-side values
 * legible rather than a pair of bare letters.
 */
export const EQUIPMENT_TYPE_OPTIONS = [
    { id: "P", text: "Power" },
    { id: "T", text: "Trailer" },
    { id: "H", text: "Material Handling" },
    { id: "F", text: "Facility" },
    { id: "C", text: "Component" },
    { id: "M", text: "Machinery" },
    { id: "I", text: "Miscellanous" },
];

/**
 * IDEQUIPMENT.MRCONTROLSTATUS (uIDM0002 / uIDD0130 - the ID module screens that own IDEQUIPMENT;
 * both agree). Numeric codes stored as text, so the ids are strings like every other list here.
 */
export const MR_CONTROL_STATUS_OPTIONS = [
    { id: "1", text: "Open, No Limits" },
    { id: "2", text: "Open, With Limits" },
    { id: "3", text: "Service Planned" },
    { id: "4", text: "Temporary Out Of Service" },
];

// ---- IDMASTERADDRESSVIEW ------------------------------------------------------------------------

/**
 * IDMASTERADDRESSVIEW.STATUS and the driver sub-table's CARRIERSTATUS (uFMM0040 Drivers /
 * uIDM0092 Shippers - both agree).
 */
export const IDENTITY_STATUS_OPTIONS = [
    { id: "A", text: "Active" },
    { id: "I", text: "Inactive" },
];

// ---- FMDISPATCHOBJECTCONTENTS -------------------------------------------------------------------

/** FMDISPATCHOBJECTCONTENTS.STATUS (uFMM0084) - two codes of its own, not the dispatch object's. */
export const CONTENTS_STATUS_OPTIONS = [
    { id: "N", text: "N/A" },
    { id: "L", text: "Loaded" },
];

// ---- FMMESSAGES ---------------------------------------------------------------------------------

/** FMMESSAGES.STATUS (uFMM0042 / uFMM0043 / uFMM0062 - all agree). */
export const MESSAGE_STATUS_OPTIONS = [
    { id: "O", text: "Open" },
    { id: "H", text: "Hold" },
    { id: "C", text: "Completed" },
];

/**
 * FMMESSAGES.TRAILER1STATUS / 2 / 3 (uFMM0042).
 *
 * The messages modules carry a third code the equipment module does not: uFMM0044 declares only
 * E and F, so 'N' would render blank if that list were reused here.
 */
export const TRAILER_STATUS_OPTIONS = [
    { id: "N", text: "None" },
    { id: "E", text: "Empty" },
    { id: "F", text: "Full" },
];
